import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User roles
export const UserRole = {
  SUPER_ADMIN: 'super_admin',
  STORE_ADMIN: 'store_admin',
  STORE_STAFF: 'store_staff',
  DELIVERY_BOY: 'delivery_boy'
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    // Restore user from localStorage immediately to prevent flash
    const stored = localStorage.getItem('userData');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      localStorage.setItem('userData', JSON.stringify(response.data));
    } catch (error) {
      // Only logout on explicit 401/403 (token invalid/expired)
      // NOT on network errors or server issues
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        logout();
      }
      // On network error, keep the cached user so session persists
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setToken(null);
    setUser(null);
  };

  const register = async (data) => {
    // Registration is now handled by Super Admin creating users
    // This legacy function is kept for backward compatibility
    // but the Register tab should ideally be hidden or redirect to admin
    throw new Error('Registration is now managed by Super Admin. Please contact your administrator.');
  };

  // Role helper functions
  const isSuperAdmin = () => user?.role === UserRole.SUPER_ADMIN;
  const isStoreAdmin = () => user?.role === UserRole.STORE_ADMIN;
  const isStoreStaff = () => user?.role === UserRole.STORE_STAFF;
  const isDeliveryBoy = () => user?.role === UserRole.DELIVERY_BOY;
  const canDelete = () => [UserRole.SUPER_ADMIN, UserRole.STORE_ADMIN].includes(user?.role);
  const canManageUsers = () => user?.role === UserRole.SUPER_ADMIN;
  const canManageStores = () => user?.role === UserRole.SUPER_ADMIN;

  return (
    <AuthContext.Provider value={{
      token,
      user,
      loading,
      login,
      logout,
      register,
      isSuperAdmin,
      isStoreAdmin,
      isStoreStaff,
      isDeliveryBoy,
      canDelete,
      canManageUsers,
      canManageStores
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
